/**
 * DropsAdvancedFilters
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { AlertTriangle, RotateCcw } from 'lucide-react';

import { DateRangePresetDropdown } from '@/components/date-range';
import { Button } from '@/components/ui';

import DropsFilterFieldGrid from './DropsFilterFieldGrid';
import DropsFilterStatsBar from './DropsFilterStatsBar';

interface DropsAdvancedFiltersProps {
  open: boolean;
  campaign: string;
  onCampaignChange: (value: string) => void;
  leadsList: string;
  onLeadsListChange: (value: string) => void;
  sender: string;
  onSenderChange: (value: string) => void;
  country: string;
  onCountryChange: (value: string) => void;
  dateFrom: string;
  onDateFromChange: (value: string) => void;
  dateTo: string;
  onDateToChange: (value: string) => void;
  onClear: () => void;
  shownCount: number;
  loadedCount: number;
  totalSmswiser: number;
  fetchEstimate?: number;
  coverageWarning?: string | null;
}

export const DropsAdvancedFilters: FC<DropsAdvancedFiltersProps> = ({
  open,
  campaign,
  onCampaignChange,
  leadsList,
  onLeadsListChange,
  sender,
  onSenderChange,
  country,
  onCountryChange,
  dateFrom,
  onDateFromChange,
  dateTo,
  onDateToChange,
  onClear,
  shownCount,
  loadedCount,
  totalSmswiser,
  fetchEstimate,
  coverageWarning,
}) => {
  const prefersReducedMotion = useReducedMotion();
  const hasDateRange = Boolean(dateFrom && dateTo);

  return (
    <AnimatePresence initial={false}>
      {open ? (
        <motion.div
          key="filters-panel"
          initial={prefersReducedMotion ? false : { height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={prefersReducedMotion ? undefined : { height: 0, opacity: 0 }}
          transition={{ duration: prefersReducedMotion ? 0 : 0.2 }}
          className="overflow-hidden"
        >
          <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-4">
            <DropsFilterFieldGrid
              campaign={campaign}
              onCampaignChange={onCampaignChange}
              leadsList={leadsList}
              onLeadsListChange={onLeadsListChange}
              sender={sender}
              onSenderChange={onSenderChange}
              country={country}
              onCountryChange={onCountryChange}
            />

            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-muted-foreground">Date range</span>
                <DateRangePresetDropdown
                  fromDate={dateFrom}
                  toDate={dateTo}
                  onFromChange={onDateFromChange}
                  onToChange={onDateToChange}
                  onDateRangeChange={(from, to) => {
                    onDateFromChange(from);
                    onDateToChange(to);
                  }}
                />
                {!hasDateRange ? (
                  <p className="text-[11px] text-muted-foreground">
                    Optional — selecting a range loads matching history from SMSWiser automatically.
                  </p>
                ) : null}
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={onClear}
                className="shrink-0 self-start sm:self-auto"
              >
                <RotateCcw className="mr-2 h-3.5 w-3.5" />
                Clear filters
              </Button>
            </div>

            {coverageWarning ? (
              <div className="flex gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2.5 text-xs leading-relaxed text-amber-900 dark:text-amber-100">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                <span>{coverageWarning}</span>
              </div>
            ) : null}

            <div className="border-t border-border/60 pt-3">
              <DropsFilterStatsBar
                shownCount={shownCount}
                loadedCount={loadedCount}
                totalSmswiser={totalSmswiser}
                fetchEstimate={fetchEstimate}
                dateRangeActive={hasDateRange}
              />
            </div>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};

export default DropsAdvancedFilters;
